import { Box } from "@mui/material";
import { OLPaper } from "OLPaper";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { hasViewAccessToResource } from "api/general";
import { useTranslations } from "api/translations";
import { FormButtons } from "components/FormControls/FormButtons";
import { OLCheckbox } from "components/FormControls/OLCheckbox";
import { TextInput } from "components/FormControls/TextInput";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { I18nContext } from "contexts/I18n/I18nContext";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useContext } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useParams } from "react-router-dom";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { useStyles } from "./LanguageForm.styles";

export function EditLanguage() {
  const classes = useStyles();
  const { id: languageCode } = useParams<{ id: string }>();
  const { refetchLanguages } = useContext(I18nContext);

  const { editLanguage, useLanguageData } = useTranslations();
  const { data: languageData } = useLanguageData(languageCode as string);
  const { message } = useMessage();
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const hasViewLanguagePermission = hasViewAccessToResource("LANGUAGE");

  return (
    <>
      <TopHeader
        title={
          languageData &&
          `${message({ id: "editLanguage" })} "${languageData?.name}"`
        }
        clearLink={links.settings}
      />
      <div>
        {languageData ? (
          <Formik
            enableReinitialize
            initialStatus={{}}
            initialValues={{
              adminDefault: languageData.adminDefault,
              apiDefault: languageData.apiDefault,
              localeCode: { code: languageData.localeCode?.code },
              name: languageData.name,
              order: languageData.order,
            }}
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              setSubmitting(true);
              setStatus({});

              try {
                await editLanguage(languageCode as string, {
                  ...(({ localeCode, ...language }) => language)(values),
                });
                refetchLanguages();
                navigate(links.settings);
                notify(message({ id: "languageUpdated" }));
              } catch (exception) {
                setSubmitting(false);
                notify(getErrorMessage(exception));
                const errors = backendErrors(getBackendErrors(exception));
                setStatus(errors);
                focusError();
              }
            }}
          >
            {({
              handleSubmit,
              isSubmitting,
              status,
              dirty,
              values,
              initialValues,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  {status?.[""] && (
                    <span className={classes.error}>{status[""]}</span>
                  )}
                  <OLPaper className={classes.OLPaper}>
                    <TextInput
                      data-test-id="code"
                      className={classes.SelectInput}
                      disabled={true}
                      label={message({ id: "code" })}
                      name="localeCode.code"
                    />
                    <TextInput
                      data-test-id="name"
                      className={classes.SelectInput}
                      disabled={hasViewLanguagePermission}
                      label={message({ id: "name" })}
                      name="name"
                    />
                    <TextInput
                      data-test-id="order"
                      className={classes.SelectInput}
                      disabled={hasViewLanguagePermission}
                      label={message({ id: "order" })}
                      name="order"
                      type="number"
                    />
                    <OLCheckbox
                      data-test-id="admin-default"
                      className={`${classes.checkbox} ${classes.SelectInput}`}
                      disabled={hasViewLanguagePermission}
                      name="adminDefault"
                      label={message({ id: "adminDefault" })}
                    />
                    <OLCheckbox
                      data-test-id="api-default"
                      className={`${classes.checkbox} ${classes.SelectInput}`}
                      disabled={hasViewLanguagePermission}
                      name="apiDefault"
                      label={message({ id: "apiDefault" })}
                    />
                  </OLPaper>
                  {!isSubmitting && (
                    <LeaveSiteGuard
                      warnBeforeRefresh
                      isDirty={dirty}
                      initialValues={initialValues}
                      currentValues={values}
                    />
                  )}
                  <Box mt={12} />
                  <FormButtons
                    disableSubmit={isSubmitting || hasViewLanguagePermission}
                    onCancelClick={() => navigate(links.settings)}
                    onSubmitClick={() => handleSubmit()}
                  />
                </Form>
              );
            }}
          </Formik>
        ) : (
          <>
            <Skeleton height={300} width={"100%"} />
            <Box mt={12} />
            <FormButtons
              disableSubmit
              onCancelClick={() => {}}
              onSubmitClick={() => {}}
            />
          </>
        )}
      </div>
    </>
  );
}
